
import React, { useState, useEffect } from 'react';
import './PublicGamesList.css';

interface PublicServer {
  id: string;
  name: string;
  url: string;
  players: number;
  maxPlayers: number;
  mode: string;
  ping?: number;
}

interface OrchestratorSession {
    gameSessionId: string;
    port: number;
    startTime: number;
    wsUrl: string;
    gameMode: string;
    maxPlayers: number;
    players: number;
    name?: string;
}

interface PublicGamesListProps {
  onJoin: (url: string) => void;
  onBack: () => void;
}

export const PublicGamesList: React.FC<PublicGamesListProps> = ({ onJoin, onBack }) => {
  const [servers, setServers] = useState<PublicServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchServers();
  }, []);

  const fetchServers = async () => {
    setLoading(true);
    setError(null);
    try {
        const orchestratorUrl = process.env.NODE_ENV === 'production' 
            ? window.location.origin 
            : "http://localhost:3001";
            
        const response = await fetch(`${orchestratorUrl}/sessions`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const sessions: OrchestratorSession[] = data.sessions || [];

        const publicServers: PublicServer[] = sessions.map(session => ({
            id: session.gameSessionId,
            name: session.name || `Game Session #${session.port}`, // Could be better if we stored a name
            url: session.wsUrl,
            players: session.players,
            maxPlayers: session.maxPlayers,
            mode: session.gameMode.charAt(0).toUpperCase() + session.gameMode.slice(1)
        }));
        
        setServers(publicServers);
    } catch (err) {
        console.error("Error fetching servers:", err);
        setError("Failed to load server list. Is the orchestrator running?");
    } finally {
        setLoading(false);
    }
  };

  return (
    <div className="public-games-list">
      <div className="header">
        <h2>Public Games</h2>
        <button onClick={fetchServers} className="refresh-btn">Refresh</button>
      </div>

      {loading && <div className="loading">Loading servers...</div>}
      
      {error && <div className="error">{error}</div>}

      {!loading && !error && servers.length === 0 && (
        <div className="no-servers">No public servers found.</div>
      )}

      <div className="server-grid">
        {servers.map(server => (
          <div key={server.id} className="server-card" onClick={() => onJoin(server.url)}>
            <div className="server-info">
                <h3>{server.name}</h3>
                <div className="server-details">
                    <span className="mode">{server.mode}</span>
                    <span className="player-count">{server.players} / {server.maxPlayers} Players</span>
                </div>
            </div>
            <div className="server-action">
                <button>Join</button>
            </div>
          </div>
        ))}
      </div>

      <div className="footer">
        <button onClick={onBack} className="back-btn">Back</button>
      </div>
    </div>
  );
};
